import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Skeleton,
  IconButton,
  Modal,
  Backdrop,
} from "@mui/material";
import {
  CalendarToday as CalendarIcon,
  Article as ArticleIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from "@mui/icons-material";
import { EarningsTranscript } from "../hooks/useEarningsTranscript";
import { QuarterOption } from "../utils/quarterUtils";
import { formatEarningsDate } from "../utils/quarterUtils";
import TranscriptModeToggle, { TranscriptMode } from "./TranscriptModeToggle";
import {
  useAiEarningsSummary,
  useGenerateAiSummary,
} from "../hooks/useAiEarningsSummary";
import { Autocomplete, TextField } from "@mui/material";
import AiSummaryContent from "./AiSummaryContent";
import { hapticImpact } from "../../../../lib/haptics";
import { getSymbolImageUrl } from "../../../../lib/symbolImage";

interface TranscriptDisplayProps {
  transcript: EarningsTranscript | null;
  symbol: string;
  companyName?: string;
  isLoading?: boolean;
  error?: Error | null;
  year: number; // Calendar year (for display)
  quarter: number; // Calendar quarter (for display)
  fiscalYear?: number; // Fiscal year (for API calls)
  fiscalQuarter?: number; // Fiscal quarter (for API calls)
  mode: TranscriptMode;
  onModeChange: (mode: TranscriptMode) => void;
  quarterOptions: QuarterOption[];
  selectedQuarter: string;
  onQuarterSelect: (quarterOption: QuarterOption) => void;
  isFullscreen?: boolean;
  onFullscreenToggle?: () => void;
  // true (default): card uses calc(100vh - offset) and scrolls internally — /earnings page
  // false: card uses flex: 1 and fills the parent container — ViewTicker
  fillHeight?: boolean;
}

const TranscriptDisplaySkeleton: React.FC = () => {
  const theme = useTheme();

  return (
    <Box>
      {/* Single unified Card - matches actual TranscriptDisplay structure */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          backgroundColor:
            theme.palette.mode === "dark" ? "#212121" : "#f5f5f5",
          border:
            theme.palette.mode === "dark"
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
          height: "calc(100vh - 160px)",
          minHeight: "600px",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        <CardContent
          sx={{
            p: 0,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
          }}
        >
          {/* Scrollable Content Container */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "scroll",
              p: 4,
              "&::-webkit-scrollbar": {
                width: 8,
              },
              "&::-webkit-scrollbar-track": {
                background:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.05)",
                borderRadius: 4,
              },
              "&::-webkit-scrollbar-thumb": {
                background:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.2)"
                    : "rgba(0, 0, 0, 0.2)",
                borderRadius: 4,
                "&:hover": {
                  background:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.3)"
                      : "rgba(0, 0, 0, 0.3)",
                },
              },
            }}
          >
            {/* Centered Report Header */}
            <Box sx={{ mb: 4, textAlign: "center" }}>
              {/* Company Logo */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 3,
                }}
              >
                <Skeleton
                  variant="circular"
                  width={80}
                  height={80}
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.1)",
                  }}
                />
              </Box>

              {/* Company Name and Quarter Title */}
              <Skeleton
                variant="text"
                width="60%"
                height={50}
                sx={{
                  mb: 2,
                  mx: "auto",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.1)",
                }}
              />

              {/* Date */}
              <Skeleton
                variant="text"
                width="30%"
                height={20}
                sx={{
                  mx: "auto",
                  mb: 3,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.08)"
                      : "rgba(0, 0, 0, 0.08)",
                }}
              />

              {/* Divider line */}
              <Box
                sx={{
                  width: "200px",
                  height: "1px",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.1)",
                  mx: "auto",
                }}
              />
            </Box>

            {/* Content Text Skeleton */}
            {Array.from({ length: 15 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="text"
                width={index % 4 === 0 ? "80%" : "100%"}
                height={20}
                sx={{
                  mb: 1,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.08)"
                      : "rgba(0, 0, 0, 0.08)",
                }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

const TranscriptDisplay: React.FC<TranscriptDisplayProps> = ({
  transcript,
  symbol,
  companyName,
  isLoading = false,
  error = null,
  year,
  quarter,
  fiscalYear,
  fiscalQuarter,
  mode,
  onModeChange,
  quarterOptions,
  selectedQuarter,
  onQuarterSelect,
  isFullscreen = false,
  onFullscreenToggle,
  fillHeight = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // AI Summary hooks - use fiscal year/quarter if available, fallback to calendar
  const aiYear = fiscalYear ?? year;
  const aiQuarter = fiscalQuarter ?? quarter;
  const {
    data: aiData,
    isLoading: aiLoading,
    error: aiError,
    isPolling,
    startPolling,
  } = useAiEarningsSummary(symbol, aiYear, aiQuarter);
  const generateMutation = useGenerateAiSummary(symbol, aiYear, aiQuarter);

  const handleGenerateAi = () => {
    hapticImpact();
    generateMutation.mutate({ onStartPolling: startPolling });
  };

  if (isLoading) {
    return <TranscriptDisplaySkeleton />;
  }

  if (error) {
    // Check if this is a 404 error (no transcript available for this company)
    const is404 = (error as any)?.response?.status === 404 ||
                  error.message?.includes('404') ||
                  error.message?.includes('Request failed with status code 404');

    if (is404) {
      // Show friendly "no transcripts" message for 404
      return (
        <Box sx={{ py: 3 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(100, 116, 139, 0.1)"
                  : "rgba(100, 116, 139, 0.05)",
              border: (theme) =>
                theme.palette.mode === "dark"
                  ? "1px solid rgba(100, 116, 139, 0.2)"
                  : "1px solid rgba(100, 116, 139, 0.1)",
            }}
          >
            <CardContent sx={{ p: 4, textAlign: "center" }}>
              <ArticleIcon
                sx={{
                  fontSize: 48,
                  color: "#64748b",
                  mb: 2,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#64748b",
                  mb: 1,
                }}
              >
                No Transcripts Available
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: (theme) =>
                    theme.palette.mode === "dark" ? "#a1a1a6" : "#86868b",
                }}
              >
                This company doesn't have any earnings call transcripts available
              </Typography>
            </CardContent>
          </Card>
        </Box>
      );
    }

    // Show error state for other errors
    return (
      <Box sx={{ py: 3 }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(239, 68, 68, 0.1)"
                : "rgba(239, 68, 68, 0.05)",
            border: (theme) =>
              theme.palette.mode === "dark"
                ? "1px solid rgba(239, 68, 68, 0.2)"
                : "1px solid rgba(239, 68, 68, 0.1)",
          }}
        >
          <CardContent sx={{ p: 4, textAlign: "center" }}>
            <ArticleIcon
              sx={{
                fontSize: 48,
                color: "#ef4444",
                mb: 2,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#ef4444",
                mb: 1,
              }}
            >
              Error Loading Transcript
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: (theme) =>
                  theme.palette.mode === "dark" ? "#a1a1a6" : "#86868b",
              }}
            >
              {error.message || "Failed to load earnings transcript"}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!transcript) {
    return (
      <Box sx={{ py: 3 }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(100, 116, 139, 0.1)"
                : "rgba(100, 116, 139, 0.05)",
            border: (theme) =>
              theme.palette.mode === "dark"
                ? "1px solid rgba(100, 116, 139, 0.2)"
                : "1px solid rgba(100, 116, 139, 0.1)",
          }}
        >
          <CardContent sx={{ p: 4, textAlign: "center" }}>
            <ArticleIcon
              sx={{
                fontSize: 48,
                color: "#64748b",
                mb: 2,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#64748b",
                mb: 1,
              }}
            >
              No Transcript Available
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: (theme) =>
                  theme.palette.mode === "dark" ? "#a1a1a6" : "#86868b",
              }}
            >
              Select a quarter from the menu to view the earnings transcript
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Format the transcript content with proper line breaks and sections
  const formatTranscriptContent = (content: string) => {
    // Replace single \n with double \n\n for better spacing, but preserve existing \n\n
    const spacedContent = content.replace(/\n(?!\n)/g, "\n\n");

    // Split by double newlines to get paragraphs with increased spacing
    const paragraphs = spacedContent
      .split("\n\n")
      .filter((p) => p.trim() !== "");

    return paragraphs.map((paragraph, paragraphIndex) => {
      const trimmedParagraph = paragraph.trim();

      // Check if paragraph contains a colon (likely speaker name)
      const colonIndex = trimmedParagraph.indexOf(":");

      if (colonIndex > 0 && colonIndex < 50) {
        // Reasonable limit for speaker names
        // Split on first colon
        const speakerName = trimmedParagraph.substring(0, colonIndex + 1);
        const speakerText = trimmedParagraph.substring(colonIndex + 1).trim();

        return (
          <Typography
            key={paragraphIndex}
            variant="body1"
            sx={{
              mb: { xs: 2, sm: 3 },
              lineHeight: { xs: 1.7, sm: 1.8 },
              color: (theme) =>
                theme.palette.mode === "dark" ? "#e5e7eb" : "#374151",
              fontSize: { xs: "0.8125rem", sm: "1rem" },
            }}
          >
            {/* Speaker name - bold and slightly larger */}
            <Box
              component="span"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "0.875rem", sm: "1.1rem" },
                color: (theme) =>
                  theme.palette.mode === "dark" ? "#f3f4f6" : "#1f2937",
              }}
            >
              {speakerName}
            </Box>
            {/* Speaker text - normal formatting */}
            {speakerText && ` ${speakerText}`}
          </Typography>
        );
      }

      // No colon found or colon too far - render normally
      return (
        <Typography
          key={paragraphIndex}
          variant="body1"
          sx={{
            mb: { xs: 2, sm: 3 },
            lineHeight: { xs: 1.7, sm: 1.8 },
            color: (theme) =>
              theme.palette.mode === "dark" ? "#e5e7eb" : "#374151",
            fontSize: { xs: "0.8125rem", sm: "1rem" },
          }}
        >
          {trimmedParagraph}
        </Typography>
      );
    });
  };

  // /earnings desktop uses a fixed calc height; mobile and ViewTicker flex-fill
  // their parent instead — a raw viewport calc can't see the native shell's
  // top/bottom chrome, so its hard-coded offset overflows behind the tab bar.
  const cardHeight = "calc(100vh - 160px)";
  const flexFill = !isFullscreen && (!fillHeight || isMobile);

  // Render Card content (used in both normal and fullscreen modes)
  const cardContent = (
    <Card
      elevation={0}
      sx={{
        borderRadius: isMobile ? 2 : 3,
        backgroundColor:
          theme.palette.mode === "dark" ? "#212121" : "#f5f5f5",
        border:
          theme.palette.mode === "dark"
            ? "1px solid rgba(255, 255, 255, 0.1)"
            : "1px solid rgba(0, 0, 0, 0.1)",
        height: isFullscreen ? "100%" : fillHeight && !isMobile ? cardHeight : undefined,
        flex: flexFill ? 1 : undefined,
        ...(flexFill && { minHeight: 0 }),
        ...(fillHeight && !isMobile && !isFullscreen && { minHeight: "600px" }),
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
        <CardContent
          sx={{
            p: 0,
            "&:last-child": { paddingBottom: 0 },
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          {/* Scrollable Content Container */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              p: { xs: 2, sm: 3, md: 4 },
              "&::-webkit-scrollbar": {
                width: isMobile ? 4 : 8,
              },
              "&::-webkit-scrollbar-track": {
                background:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.05)",
                borderRadius: 4,
              },
              "&::-webkit-scrollbar-thumb": {
                background:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.2)"
                    : "rgba(0, 0, 0, 0.2)",
                borderRadius: 4,
                "&:hover": {
                  background:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.3)"
                      : "rgba(0, 0, 0, 0.3)",
                },
              },
            }}
          >
            {/* Report Header - Centered like a proper report */}
            {transcript && (
              <Box sx={{ mb: { xs: 2, sm: 4 }, textAlign: "center", position: "relative" }}>
                {/* Fullscreen Button - Top Right */}
                {onFullscreenToggle && (
                  <IconButton
                    onClick={onFullscreenToggle}
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                      "&:hover": {
                        color: theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.05)",
                      },
                    }}
                    aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                  >
                    {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                  </IconButton>
                )}

                {/* Company Logo */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mb: { xs: 2, sm: 3 },
                  }}
                >
                  <Box
                    component="img"
                    src={getSymbolImageUrl(symbol)}
                    alt={symbol}
                    sx={{
                      width: { xs: 56, sm: 80 },
                      height: { xs: 56, sm: 80 },
                      borderRadius: { xs: 1.5, sm: 2 },
                      objectFit: "contain",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.02)",
                      border:
                        theme.palette.mode === "dark"
                          ? "1px solid rgba(255, 255, 255, 0.1)"
                          : "1px solid rgba(0, 0, 0, 0.1)",
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      const fallback = target.nextElementSibling as HTMLElement;
                      if (fallback) {
                        fallback.textContent = symbol?.charAt(0) ?? '?';
                        fallback.style.display = "flex";
                      }
                    }}
                  />
                  <Box
                    sx={{
                      display: "none",
                      alignItems: "center",
                      justifyContent: "center",
                      width: { xs: 56, sm: 80 },
                      height: { xs: 56, sm: 80 },
                      borderRadius: { xs: 1.5, sm: 2 },
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(34, 197, 94, 0.1)"
                          : "rgba(34, 197, 94, 0.05)",
                      border:
                        theme.palette.mode === "dark"
                          ? "2px solid rgba(34, 197, 94, 0.2)"
                          : "2px solid rgba(34, 197, 94, 0.1)",
                      fontSize: { xs: "1.5rem", sm: "2rem" },
                      fontWeight: 700,
                      color: "#22c55e",
                    }}
                  ></Box>
                </Box>

                {/* Company Name and Quarter Title - Centered */}
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color:
                      theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
                    mb: { xs: 1, sm: 2 },
                    fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2.125rem" },
                    lineHeight: 1.2,
                  }}
                >
                  {companyName || symbol} - Q{quarter} {year} Earnings Call
                </Typography>

                {/* Date - Centered below title */}
                <Typography
                  variant="body1"
                  sx={{
                    color:
                      theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    fontWeight: 500,
                    fontSize: { xs: "0.8125rem", sm: "1rem" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: { xs: 0.5, sm: 1 },
                  }}
                >
                  <CalendarIcon sx={{ fontSize: { xs: 14, sm: 18 } }} />
                  {formatEarningsDate(transcript.date)}
                </Typography>

                {/* Divider line */}
                <Box
                  sx={{
                    width: { xs: "120px", sm: "200px" },
                    height: "1px",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.1)",
                    mx: "auto",
                    mt: { xs: 2, sm: 3 },
                  }}
                />
              </Box>
            )}

            {/* Conditional Content Based on Mode */}
            {mode === "transcript" ? (
              formatTranscriptContent(transcript.content)
            ) : (
              <AiSummaryContent
                symbol={symbol}
                year={aiYear}
                quarter={aiQuarter}
                aiData={aiData}
                aiLoading={aiLoading}
                aiError={aiError}
                generateMutation={generateMutation}
                onGenerateAi={handleGenerateAi}
                isPolling={isPolling}
              />
            )}
          </Box>
        </CardContent>
      </Card>
  );

  // If fullscreen, wrap in Modal
  if (isFullscreen) {
    return (
      <Modal
        open={isFullscreen}
        onClose={onFullscreenToggle}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.75)", // Darkened backdrop but not too dark
            },
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            // Safe-area aware: on native iOS the viewport includes the notch
            // and home indicator, so a vertically-centred 95vh box slides
            // under the status bar. Anchor below the top inset instead and
            // shrink by both insets — env() is 0 in ordinary browsers, so
            // web keeps the same 2.5dvh top margin / 95dvh height it had.
            top: "calc(2.5dvh + env(safe-area-inset-top, 0px))",
            left: "50%",
            transform: "translateX(-50%)",
            width: "98%", // Nearly full-width
            maxWidth: "2000px", // Max width for very large screens
            height:
              "calc(95dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))",
            outline: "none", // Remove focus outline on modal
          }}
        >
          {cardContent}
        </Box>
      </Modal>
    );
  }

  // Normal view — flex-fill paths use flex:1 (not height:100%) because this Box is a
  // flex item inside ViewCompanyEarningsTranscript's flex column; height:100% doesn't resolve vertically there.
  return (
    <Box sx={flexFill ? { flex: 1, display: "flex", flexDirection: "column", minHeight: 0 } : undefined}>
      {cardContent}
    </Box>
  );
};

export default TranscriptDisplay;
